import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import './BillViewPage.css';

function normalizeApiBase(raw) {
  const base = String(raw || '').trim() || 'https://backend.menubazzar.com/api';
  const noTrailing = base.replace(/\/+$/, '');
  // Accept either https://host/api or https://host
  if (noTrailing.endsWith('/api')) return noTrailing;
  return `${noTrailing}/api`;
}

export default function BillViewPage() {
  const { shareToken } = useParams();
  const [iframeError, setIframeError] = useState(false);
  const [loadError, setLoadError] = useState('');

  const apiBase = useMemo(
    () => normalizeApiBase(process.env.REACT_APP_API_URL),
    [],
  );

  const pdfUrl = useMemo(() => {
    if (!shareToken) return '';
    return `${apiBase}/bills/public/${encodeURIComponent(shareToken)}/pdf`;
  }, [apiBase, shareToken]);

  useEffect(() => {
    let cancelled = false;
    setLoadError('');
    setIframeError(false);

    if (!pdfUrl) return () => {};

    // Validate that the endpoint returns a PDF. If it returns HTML (404 page) many browsers show a blank iframe.
    (async () => {
      try {
        const res = await fetch(pdfUrl, {
          method: 'GET',
          headers: { Accept: 'application/pdf' },
        });

        const contentType = res.headers.get('content-type') || '';
        if (!res.ok) {
          if (!cancelled) setLoadError(`Failed to load PDF (HTTP ${res.status}).`);
          return;
        }
        if (!contentType.toLowerCase().includes('application/pdf')) {
          if (!cancelled) setLoadError('PDF endpoint did not return a PDF.');
        }
      } catch (e) {
        if (!cancelled) setLoadError(e?.message || 'Failed to load PDF.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);

  return (
    <div className="bill-view-page">
      <div className="bill-view-header">
        Bill
        <div className="bill-view-sub">View / Download your invoice</div>
      </div>

      <div className="bill-view-body">
        {!shareToken && (
          <div className="bill-view-alert">Missing bill token in URL.</div>
        )}

        {shareToken && (loadError || iframeError) && (
          <div className="bill-view-alert">
            {loadError || 'Could not load the PDF in an embedded view.'} Try opening it directly:{' '}
            <a href={pdfUrl} target="_blank" rel="noreferrer">
              Open PDF
            </a>
          </div>
        )}

        {shareToken && (
          <iframe
            className="bill-view-iframe"
            title="Bill PDF"
            src={pdfUrl}
            onError={() => setIframeError(true)}
          />
        )}
      </div>
    </div>
  );
}
